/**
 * Apparition au scroll — fadein / fadeinup.
 * Format : .selector|type|delay  ou  .selector|delay (legacy → fadeinup)
 */
(function () {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const REVEAL_OBSERVER_OPTIONS_DESKTOP = {
    rootMargin: '0px 0px -20% 0px',
    threshold: 0.15
  };

  const REVEAL_OBSERVER_OPTIONS_MOBILE = {
    rootMargin: '0px 0px -10% 0px',
    threshold: 0.1
  };

  const REVEAL_MOBILE_MEDIA_QUERY = window.matchMedia('(max-width: 999px)');

  function getRevealObserverOptions() {
    return REVEAL_MOBILE_MEDIA_QUERY.matches
      ? REVEAL_OBSERVER_OPTIONS_MOBILE
      : REVEAL_OBSERVER_OPTIONS_DESKTOP;
  }

  function getRevealViewportBounds() {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const margins = getRevealObserverOptions().rootMargin.split(/\s+/).map((value) => value.trim());

    function parseMargin(value, axisSize) {
      if (!value || value === '0' || value === '0px') return 0;
      if (value.endsWith('%')) {
        return (parseFloat(value) / 100) * axisSize;
      }
      return parseFloat(value) || 0;
    }

    const marginTop = parseMargin(margins[0] || '0px', viewportHeight);
    const marginRight = parseMargin(margins[1] || '0px', viewportWidth);
    const marginBottom = parseMargin(margins[2] || '0px', viewportHeight);
    const marginLeft = parseMargin(margins[3] || '0px', viewportWidth);

    return {
      top: marginTop,
      left: marginLeft,
      right: viewportWidth + marginRight,
      bottom: viewportHeight + marginBottom
    };
  }

  function getIntersectionRatio(rect, viewport) {
    const intersectionTop = Math.max(rect.top, viewport.top);
    const intersectionLeft = Math.max(rect.left, viewport.left);
    const intersectionBottom = Math.min(rect.bottom, viewport.bottom);
    const intersectionRight = Math.min(rect.right, viewport.right);
    const width = Math.max(0, intersectionRight - intersectionLeft);
    const height = Math.max(0, intersectionBottom - intersectionTop);
    const elementArea = rect.width * rect.height;

    if (elementArea <= 0) return 0;

    return (width * height) / elementArea;
  }

  function isSectionInRevealViewport(root) {
    const rect = root.getBoundingClientRect();
    const viewport = getRevealViewportBounds();
    return getIntersectionRatio(rect, viewport) >= getRevealObserverOptions().threshold;
  }

  function parseDelay(value) {
    return Number(String(value || '0').replace(/ms/gi, '')) || 0;
  }

  function normalizeAnimation(type) {
    const normalized = String(type || 'fadeinup').toLowerCase().trim();
    return normalized === 'fadein' ? 'fadein' : 'fadeinup';
  }

  function parseTargetLine(line) {
    const parts = line.split('|').map((part) => part.trim());
    const selector = parts[0] || '';

    if (!selector) return null;

    let animation = 'fadeinup';
    let delay = 0;

    if (parts.length >= 3) {
      animation = normalizeAnimation(parts[1]);
      delay = parseDelay(parts[2]);
    } else if (parts.length === 2) {
      if (/^\d/.test(parts[1]) || /ms$/i.test(parts[1])) {
        delay = parseDelay(parts[1]);
      } else {
        animation = normalizeAnimation(parts[1]);
      }
    }

    return { selector, animation, delay };
  }

  function parseTargetsRaw(raw) {
    if (!raw || typeof raw !== 'string') return [];

    const normalized = raw
      .replace(/\r/g, '')
      .replace(/;/g, '\n')
      .replace(/ \./g, '\n.');

    return normalized
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map(parseTargetLine)
      .filter(Boolean);
  }

  function revealElement(el) {
    el.classList.add('is-revealed');
  }

  function scheduleReveal(el, delay) {
    const ms = Number(delay) || 0;
    if (prefersReducedMotion || ms <= 0) {
      revealElement(el);
      return;
    }
    setTimeout(() => revealElement(el), ms);
  }

  function parseConfig(root) {
    const sectionEl = root.closest('[id^="shopify-section-"]');
    const configScript = sectionEl
      ? sectionEl.querySelector('script[data-scroll-reveal-config]')
      : null;

    if (configScript && configScript.textContent) {
      try {
        const config = JSON.parse(configScript.textContent);
        if (config.targetsRaw) {
          config.targets = parseTargetsRaw(config.targetsRaw);
        }
        return config;
      } catch (error) {
        /* fallback attribut legacy */
      }
    }

    try {
      const config = JSON.parse(root.getAttribute('data-scroll-reveal-config') || '{}');
      if (config.targetsRaw) {
        config.targets = parseTargetsRaw(config.targetsRaw);
      }
      return config;
    } catch (error) {
      return {};
    }
  }

  function getCascadeItems(root, selector) {
    if (!selector) return [];

    if (root.querySelector('.slick-initialized')) {
      const nested = Array.from(
        root.querySelectorAll('.slick-slide:not(.slick-cloned) ' + selector)
      );
      if (nested.length) return nested;

      const className = selector.replace(/^\./, '');
      return Array.from(
        root.querySelectorAll('.slick-slide:not(.slick-cloned).' + className)
      );
    }

    return Array.from(root.querySelectorAll(selector));
  }

  function markScrollReveal(el, animation) {
    const type = normalizeAnimation(animation);
    el.classList.add('scroll-reveal');
    el.classList.remove('scroll-reveal--fadein', 'scroll-reveal--fadeinup');
    el.classList.add(type === 'fadein' ? 'scroll-reveal--fadein' : 'scroll-reveal--fadeinup');
  }

  function prepareTargets(root, config) {
    (config.targets || []).forEach((target) => {
      if (!target || !target.selector) return;

      root.querySelectorAll(target.selector).forEach((el) => {
        markScrollReveal(el, target.animation);
      });
    });

    const cascade = config.cascade || {};
    if (!cascade.enabled || !cascade.selector) return;

    getCascadeItems(root, cascade.selector).forEach((el) => {
      markScrollReveal(el, 'fadeinup');
    });
  }

  function whenReady(root, config, callback) {
    if (!config.cascade || !config.cascade.waitSlick) {
      callback();
      return;
    }

    if (root.querySelector('.slick-initialized')) {
      callback();
      return;
    }

    let attempts = 0;
    const maxAttempts = 60;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (root.querySelector('.slick-initialized') || attempts >= maxAttempts) {
        window.clearInterval(interval);
        callback();
      }
    }, 50);
  }

  function runReveal(root, config) {
    (config.targets || []).forEach((target) => {
      if (!target || !target.selector) return;

      root.querySelectorAll(target.selector).forEach((el) => {
        scheduleReveal(el, target.delay);
      });
    });

    const cascade = config.cascade || {};
    if (!cascade.enabled || !cascade.selector) return;

    const baseDelay = Number(cascade.baseDelay) || 0;
    const step = Number(cascade.step) || 0;

    getCascadeItems(root, cascade.selector).forEach((el, index) => {
      scheduleReveal(el, baseDelay + step * index);
    });
  }

  function resetSection(root) {
    root.querySelectorAll('.scroll-reveal').forEach((el) => {
      el.classList.remove('is-revealed', 'scroll-reveal', 'scroll-reveal--fadein', 'scroll-reveal--fadeinup');
    });
    delete root.dataset.scrollRevealDone;
  }

  function initSection(root, force) {
    if (!root || !root.hasAttribute('data-scroll-reveal-section')) return;

    const config = parseConfig(root);
    if (!config.enabled) return;

    if (root.dataset.scrollRevealDone === 'true' && !force) return;

    if (force) {
      resetSection(root);
    }

    prepareTargets(root, config);

    if (root._scrollRevealObserver) {
      root._scrollRevealObserver.disconnect();
      delete root._scrollRevealObserver;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        observer.disconnect();
        delete root._scrollRevealObserver;

        whenReady(root, config, () => {
          prepareTargets(root, config);
          runReveal(root, config);
          root.dataset.scrollRevealDone = 'true';
        });
      });
    }, getRevealObserverOptions());

    observer.observe(root);
    root._scrollRevealObserver = observer;

    if (force && root.dataset.scrollRevealDone !== 'true' && isSectionInRevealViewport(root)) {
      whenReady(root, config, () => {
        prepareTargets(root, config);
        runReveal(root, config);
        root.dataset.scrollRevealDone = 'true';
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-scroll-reveal-section]').forEach((root) => {
      initSection(root);
    });
  }

  window.ScrollReveal = {
    init: initAll,
    initSection: initSection
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-scroll-reveal-section]');
    if (root) {
      initSection(root, true);
    }
  });
})();
